import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { usePmt3FarmingSeedsData } from '@/hooks/usePmt3FarmingSeedsData';
import { usePMT3Selection } from '@/context/PMT3SelectionContext';

// Helper function to chunk an array into smaller arrays
const chunkArray = <T,>(array: T[], chunkSize: number): T[][] => {
  const result: T[][] = [];
  for (let i = 0; i < array.length; i += chunkSize) {
    result.push(array.slice(i, i + chunkSize));
  }
  return result;
};

// Component to render a single column of seeds
interface SeedColumnProps {
    items: string[];
}
const SeedColumn: React.FC<SeedColumnProps> = ({ items }) => (
    // Added min-h-[1rem] to prevent collapse when empty, adjust as needed
    <div className="min-h-[1rem]">
        {items.map((item, index) => (
            <div key={index} className="text-xs py-0.5 truncate" title={item}> {/* Added title for full view on hover */}
                {item || <>&nbsp;</>} {/* Render space if item is empty string to maintain layout */}
            </div>
        ))}
    </div>
);


const FarmingSeedsDashboard: React.FC = () => {
    const { selectedCompany } = usePMT3Selection();
    const { ctaSeedsEntries, uniqueWordsEntries, isLoading, error } = usePmt3FarmingSeedsData(selectedCompany);

    // Chunk the arrays into 3 columns each
    const ctaChunks = chunkArray(ctaSeedsEntries, Math.ceil(ctaSeedsEntries.length / 3));
    const uniqueChunks = chunkArray(uniqueWordsEntries, Math.ceil(uniqueWordsEntries.length / 3));

    // Ensure we always have 3 chunks, even if empty, for consistent grid layout
    while (ctaChunks.length < 3) ctaChunks.push([]);
    while (uniqueChunks.length < 3) uniqueChunks.push([]);


    if (error) {
        // Use standard title size
        return <Card><CardHeader><CardTitle className="text-2xl font-bold">Farming Seeds</CardTitle></CardHeader><CardContent><p className="text-red-600 text-sm">Error loading data: {error.message}</p></CardContent></Card>;
    }

    return (
        <Card>
            <CardHeader className="p-4">
                 {/* Updated Title and Size */}
                <CardTitle className="text-2xl font-bold">Farming Seeds</CardTitle>
            </CardHeader>
            <CardContent className="p-4">
                {isLoading ? (
                    // Skeleton remains the same
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <Skeleton className="h-6 w-3/4 mb-2" /> {/* Adjusted skeleton height */}
                            <div className="grid grid-cols-3 gap-x-3">
                                <Skeleton className="h-40 w-full" /> {/* Adjusted skeleton height */}
                                <Skeleton className="h-40 w-full" />
                                <Skeleton className="h-40 w-full" />
                            </div>
                        </div>
                         <div>
                            <Skeleton className="h-6 w-3/4 mb-2" /> {/* Adjusted skeleton height */}
                            <div className="grid grid-cols-3 gap-x-3">
                                <Skeleton className="h-40 w-full" /> {/* Adjusted skeleton height */}
                                <Skeleton className="h-40 w-full" />
                                <Skeleton className="h-40 w-full" />
                            </div>
                        </div>
                    </div>
                ) : (
                    // Main layout with divider
                    <div className="grid grid-cols-1 md:grid-cols-2 md:gap-x-6">
                        {/* CTAs and Broad Seeds Section */}
                        <div className="mb-4 md:mb-0"> {/* Add bottom margin for mobile */}
                             {/* Updated Sub-Title Size */}
                            <h3 className="font-semibold mb-2 text-lg border-b pb-1"> {/* Changed to text-lg */}
                                CTAs and Broad Seeds ({ctaSeedsEntries.length})
                            </h3>
                            <div className="grid grid-cols-3 gap-x-3">
                                {ctaChunks.map((chunk, index) => (
                                    <SeedColumn key={`cta-chunk-${index}`} items={chunk} />
                                ))}
                            </div>
                        </div>

                        {/* Unique Words Section with Divider */}
                         {/* Added border-l and padding for divider effect on medium screens+ */}
                        <div className="md:border-l md:pl-6 border-gray-300 dark:border-gray-700">
                             {/* Updated Sub-Title Size */}
                             <h3 className="font-semibold mb-2 text-lg border-b pb-1"> {/* Changed to text-lg */}
                                Unique Words ({uniqueWordsEntries.length})
                            </h3>
                             <div className="grid grid-cols-3 gap-x-3">
                                {uniqueChunks.map((chunk, index) => (
                                    <SeedColumn key={`unique-chunk-${index}`} items={chunk} />
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default FarmingSeedsDashboard;